import { EUnit, cmToInch, dpmToInch, pcToInch, ptToInch, pxToInch } from '../../../independent/unit';
import { ImageTypes } from '../constant/image-type';

// 设置默认DPI值
const DEFAULT_DPI = 72;

// 从单个图像Blob中获取垂直DPI
export function getVerticalDpiFromSingleImageBlob(imageBlob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
        // 创建一个FileReader来读取图像Blob
        const reader = new FileReader();
        // 当FileReader成功读取Blob时
        reader.onload = () => {
            // 将FileReader的结果转换为ArrayBuffer
            const arrayBuffer = reader.result as ArrayBuffer;
            // 将ArrayBuffer转换为Uint8Array
            const uint8Array = new Uint8Array(arrayBuffer);
            // 获取图像类型
            const type = getImageType(uint8Array);
            let dpi = DEFAULT_DPI;
            if (type) {
                dpi = getVerticalDpi(type, uint8Array) ?? dpi;
            }
            // 返回DPI值
            resolve(dpi);
        };
        // 当FileReader读取Blob时出错
        reader.onerror = () => {
            reject(reader.error);
        };
        // 以ArrayBuffer形式读取图像Blob
        reader.readAsArrayBuffer(imageBlob);
    });
}

// 获取图像类型
function getImageType(uint8Array: Uint8Array): ImageTypes | null {
    // 定义图像类型及其签名
    const imageTypes = [
        { signature: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], type: ImageTypes.PNG },
        { signature: [0xFF, 0xD8, 0xFF], type: ImageTypes.JPEG },
        { signature: [0x49, 0x49, 0x2A, 0x00], type: ImageTypes.TIFF },
        { signature: [0x4D, 0x4D, 0x00, 0x2A], type: ImageTypes.TIFF },
        { signature: [0x42, 0x4D], type: ImageTypes.BMP },
        { signature: [0x3C, 0x3F, 0x78, 0x6D, 0x6C], type: ImageTypes.SVG },
        { signature: [0x47, 0x49, 0x46, 0x38], type: ImageTypes.GIF },
    ];

    // 遍历图像类型数组
    for (const imageType of imageTypes) {
        // 如果Uint8Array的前缀与图像类型的签名匹配，则返回该图像类型
        if (compareBytes(uint8Array.subarray(0, imageType.signature.length), imageType.signature)) {
            return imageType.type;
        }
    }

    // 如果没有匹配的图像类型，返回null
    return null;
}

// 比较两个字节数组是否相等
function compareBytes(a: Uint8Array, b: Uint8Array | number[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

// 获取垂直DPI
function getVerticalDpi(type: string, uint8Array: Uint8Array): number | null {
    const typeToDpiFunctionMap = {
        [ImageTypes.PNG]: getPngVerticalDpi,
        [ImageTypes.JPEG]: getJpegVerticalDpi,
        [ImageTypes.TIFF]: getTiffVerticalDpi,
        [ImageTypes.BMP]: getBmpVerticalDpi,
        [ImageTypes.SVG]: getSvgVerticalDpi,
        [ImageTypes.GIF]: getGifVerticalDpi,
    };
    return typeToDpiFunctionMap[type]?.(uint8Array) ?? null;
}

// 获取PNG图像的垂直DPI
function getPngVerticalDpi(uint8Array: Uint8Array): number | null {
    // 创建一个Uint8Array，表示pHYs块的类型（chunkType）
    const chunkType = new Uint8Array([0x70, 0x48, 0x59, 0x73]); // pHYs chunk
    // 使用findChunk函数在uint8Array中查找pHYs块的起始位置（chunkStart）
    const chunkStart = findChunk(uint8Array, chunkType);
    // 如果未找到pHYs块（chunkStart === -1），则返回null
    if (chunkStart === -1) {
        return null;
    }
    // 如果找到了pHYs块，从块开始位置加8个字节处读取垂直像素每米数（yPixelsPerMeter）
    // pHYs块的前8个字节分别是长度（4字节）和块类型（4字节）
    const yPixelsPerMeter = readUint32(uint8Array, chunkStart + 8);
    // 将垂直像素每米数（DPM）转换为垂直像素每英寸数（DPI）
    const dpi = dpmToInch(yPixelsPerMeter);
    return Math.round(dpi);
}

// 获取JPEG图像的垂直DPI
function getJpegVerticalDpi(uint8Array: Uint8Array): number | null {
    // 使用findStartOfApp0函数在uint8Array中查找APP0标记的起始位置（startOfApp0）
    const startOfApp0 = findStartOfApp0(uint8Array);
    if (startOfApp0 === -1) {
        return null;
    }

    // 从APP0标记开始位置加11个字节处读取密度单位（densityUnits）
    // 根据JPEG文件格式规范，密度单位存储在APP0标记的第12个字节（从0开始计数）
    const densityUnits = uint8Array[startOfApp0 + 11];
    // 从APP0标记开始位置加12个字节处读取垂直像素密度（yDensity）
    // 根据JPEG文件格式规范，垂直像素密度存储在APP0标记的第13-14个字节（从0开始计数）
    const yDensity = readUint16(uint8Array, startOfApp0 + 12);

    // 根据密度单位处理垂直像素密度值
    if (densityUnits === 1) { // DPI（每英寸点数）
        return yDensity;
    } if (densityUnits === 2) { // DPCM（每厘米点数）
        // 将垂直像素每厘米数（DPCM）转换为垂直像素每英寸数（DPI）
        return dpmToInch(yDensity * 100);
    }
    return null;
}

// 在Uint8Array中查找JPEG APP0标记的起始位置
function findStartOfApp0(uint8Array: Uint8Array): number {
    // 初始化偏移量为2，因为JPEG文件以0xFFD8（SOI标记）开始，我们从其后开始查找
    let offset = 2;
    // 遍历uint8Array
    while (offset < uint8Array.length) {
        // 读取当前位置的标记值（2字节）
        const marker = readUint16(uint8Array, offset);
        // 读取当前位置后2字节的长度值（2字节）
        const length = readUint16(uint8Array, offset + 2);
        // 如果找到了APP0标记（0xFFE0），返回当前偏移量
        if (marker === 0xFFE0) {
            return offset;
        }
        // 更新偏移量以跳过当前标记及其数据
        offset += 2 + length;
    }
    // 如果找不到APP0标记，返回-1
    return -1;
}

// 获取TIFF图像的垂直DPI
function getTiffVerticalDpi(uint8Array: Uint8Array): number | null {
    // 读取TIFF文件的字节顺序，存储在文件的前两个字节（0x4949：小端，0x4D4D：大端）
    const byteOrder = readUint16(uint8Array, 0);
    // 读取图像文件目录（IFD）的偏移量，存储在文件的第4-7个字节
    const ifdOffset = readUint32(uint8Array, 4, byteOrder);
    // 读取IFD中的条目数量，存储在IFD偏移量处
    const numEntries = readUint16(uint8Array, ifdOffset, byteOrder);
    // 遍历IFD条目
    for (let i = 0; i < numEntries; i++) {
        // 计算当前IFD条目的偏移量
        const entryOffset = ifdOffset + 2 + (i * 12);
        // 读取当前IFD条目的标签（2字节）
        const tag = readUint16(uint8Array, entryOffset, byteOrder);
        // 如果找到了垂直分辨率（YResolution）标签（0x011A）
        if (tag === 0x011A) {
            // 读取YResolution值的偏移量，存储在IFD条目的第8-11个字节
            const valueOffset = readUint32(uint8Array, entryOffset + 8, byteOrder);
            // 读取YResolution值，存储在值偏移量的后2字节（RATIONAL类型，分子在前，分母在后）
            const dpi = readUint16(uint8Array, valueOffset + 2, byteOrder);
            return dpi;
        }
    }
    return null;
}

// 获取BMP图像的垂直DPI
function getBmpVerticalDpi(uint8Array: Uint8Array): number | null {
    // 从uint8Array的第38个字节处读取垂直像素每米数（DPM）
    // 根据BMP文件格式规范，垂直像素每米数（DPM）存储在文件头的第38-41字节
    const dpi = readUint32(uint8Array, 38);
    return dpmToInch(dpi);
}

// 获取SVG图像的垂直DPI
function getSvgVerticalDpi(uint8Array: Uint8Array): number | null {
    const match = /<svg[^>]*\s+height\s*=\s*['"]([\d.]+)(in|cm|mm|pt|pc|px)['"][^>]*>/i.exec(uint8ArrayToString(uint8Array));
    if (!match) {
        return null;
    }
    const height = parseFloat(match[1]);
    const unit = match[2] as EUnit;
    const dpi = getDpiFromSvgUnit(height, unit);
    return dpi;
}

// GIF GIF格式没有明确的DPI信息
function getGifVerticalDpi(): number | null {
    return DEFAULT_DPI; // Default DPI for GIF
}

// 在Uint8Array中查找特定类型的块
function findChunk(uint8Array: Uint8Array, chunkType: Uint8Array): number {
    let offset = 8;
    // 遍历Uint8Array
    while (offset < uint8Array.length) {
        // 读取块长度
        const length = readUint32(uint8Array, offset);
        // 读取块类型
        const type = uint8Array.subarray(offset + 4, offset + 8);
        // 如果找到了匹配的类型，返回当前偏移量
        if (compareBytes(type, chunkType)) {
            return offset;
        }
        // 更新偏移量以跳过当前块
        offset += length + 12;
    }
    // 如果找不到匹配的类型，返回-1
    return -1;
}

// 从Uint8Array的指定偏移量处读取16位无符号整数
function readUint16(uint8Array: Uint8Array, offset: number, byteOrder = 0x4D4D): number {
    const a = uint8Array[offset];
    const b = uint8Array[offset + 1];
    // 根据字节顺序解析整数
    if (byteOrder === 0x4949) {
        return (b << 8) | a;
    }
    return (a << 8) | b;
}

// 从Uint8Array的指定偏移量处读取32位无符号整数
function readUint32(uint8Array: Uint8Array, offset: number, byteOrder = 0x4D4D): number {
    const a = readUint16(uint8Array, offset, byteOrder);
    const b = readUint16(uint8Array, offset + 2, byteOrder);
    // 根据字节顺序解析整数
    if (byteOrder === 0x4949) {
        return (b << 16) | a;
    }
    return (a << 16) | b;
}

// 将Uint8Array转换为字符串
function uint8ArrayToString(uint8Array: Uint8Array): string {
    const decoder = new TextDecoder();
    return decoder.decode(uint8Array);
}

// 使用枚举类型替换字符串类型
function getDpiFromSvgUnit(length: number, unit: EUnit): number {
    const unitToConversionFunctionMap = {
        [EUnit.IN]: (length: number) => length,
        [EUnit.CM]: cmToInch,
        [EUnit.MM]: (length: number) => cmToInch(length) * 10,
        [EUnit.PT]: ptToInch,
        [EUnit.PC]: pcToInch,
        [EUnit.PX]: pxToInch,
    };
    return unitToConversionFunctionMap[unit]?.(length) ?? DEFAULT_DPI;
}
